import { useState } from "react";
import { Database, CheckCircle2, AlertTriangle, ChevronDown } from "lucide-react";
import { useData } from "@/context/DataContext";
import { PageHeader, Card, SectionTitle } from "@/components/common/PageHeader";
import { StatusChip } from "@/components/common/StatusChip";
import { ScoreGauge } from "@/components/common/ScoreGauge";
import { Loading } from "@/pages/_shared";

const RESOLVE = {
  "Address consistency": "Fix the USPS suite-number mismatch in the Business Identity Center, then re-run this check to clear the warning.",
  "Beneficial ownership": "Add the missing beneficial owner (name, ownership %, ID) in the Business Identity Center to reach full KYC completeness.",
};

export default function DataIntegrity() {
  const { snapshot } = useData();
  const [open, setOpen] = useState(null);
  if (!snapshot) return <Loading />;
  const di = snapshot.data_integrity;
  const warns = di.checks.filter((c) => c.status === "WARN").length;
  const passing = di.checks.length - warns;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="data-integrity-page">
      <PageHeader eyebrow="Data Integrity Center" title="Data Integrity Center"
        subtitle="Cross-source consistency checks keep your Financial Graph clean and lender-ready. Every conflict is surfaced, never hidden."
        badge={<StatusChip state="LIVE" />} />

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="flex flex-col items-center justify-center p-6">
          <SectionTitle icon={Database}>Integrity Score</SectionTitle>
          <ScoreGauge score={di.score} scale={100} tier={warns === 0 ? "Clean" : `${warns} warning${warns !== 1 ? "s" : ""}`} size={150} testId="integrity-gauge" />
          <div className="mt-3 flex gap-2">
            <span className="rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-[11px] font-bold text-emerald-600">{passing} passing</span>
            {warns > 0 && <span className="rounded-md bg-amber-50 px-2 py-0.5 font-mono text-[11px] font-bold text-amber-600">{warns} to resolve</span>}
          </div>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <SectionTitle icon={Database}>Consistency Checks</SectionTitle>
          <div className="space-y-2">
            {di.checks.map((c) => {
              const warn = c.status === "WARN";
              const isOpen = open === c.name;
              return (
                <div key={c.name} className={`rounded-xl border ${warn ? "border-amber-200" : "border-slate-100"}`} data-testid="integrity-check-row">
                  <button onClick={() => warn && setOpen(isOpen ? null : c.name)} className={`flex w-full items-center justify-between px-4 py-3 text-left ${warn ? "" : "cursor-default"}`}>
                    <div className="flex items-center gap-3">
                      {warn ? <AlertTriangle size={17} className="text-amber-500" /> : <CheckCircle2 size={17} className="text-emerald-500" />}
                      <div><div className="text-[13px] font-medium text-slate-800">{c.name}</div><div className="font-mono text-[10px] text-slate-400">{c.sources}</div></div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusChip state={c.status} />
                      {warn && <ChevronDown size={16} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />}
                    </div>
                  </button>
                  {warn && isOpen && (
                    <div className="border-t border-amber-100 px-4 py-3" data-testid="integrity-resolve">
                      <p className="text-[12px] text-slate-600">{RESOLVE[c.name] || "Review the conflicting sources and update from the authoritative record."}</p>
                      <button className="mt-3 rounded-lg bg-[#1a5fd0] px-3.5 py-1.5 text-[12px] font-semibold text-white">Resolve now</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
